import { Permission } from '@Constants/Permission.g';
import { useHasPermission } from '@Contexts/appContext';
import {
    type ApiRequisition,
    type ApiRequisitionContainerAction,
    type ApiRequisitionPosition,
    EnumContainerActionRequest,
    EnumContainerOrderAction,
} from '@Model';
import { useContainerPlanning, useContainerTypeDetails } from '@Modules/Orders/Editing/Containers';
import { type AsyncStateUpdate, useEditSubObject } from '@synqo/frontend-component-library';
import {
    fromApiAction,
    isContainerAction,
    isExchangeHalf,
    type RequisitionAction,
    toActionRequest,
} from '@utils/requisitionAction';
import { useCallback, useMemo, useRef, useState } from 'react';
import { type ContainerPosition, type ContainerStockLine, initialCountOf, MIN_COUNT } from './containerOrder';
import { type ContainerSlot, type LinkNaming, useContainerSlot } from './useContainerSlot';

export interface ContainerOrder {
    /** The action the order carries, undefined until the customer picked a tile. */
    action: RequisitionAction | undefined;
    /** The first container action of the order and, on an exchange, the second. */
    slots: readonly [ContainerSlot, ContainerSlot];
    /** Every container the order is about, in the order of the slots. */
    positions: readonly ContainerPosition[];
    /** How many containers the order is about: one, or two on an exchange. */
    positionCount: number;
    /**
     * Takes the action. Whatever the previous one produced is dropped, because it only means
     * anything under the action it was entered for. Clicking twice takes the second answer rather
     * than minting a second order.
     */
    chooseAction: (action: RequisitionAction) => void;
    /** Has the server plan the action the order carries for a recorded line, in place of what it holds — one click in the stock list. */
    takeStockLine: (line: ContainerStockLine) => void;
    /** Drops the container and its content from the first action. The action stays. */
    removeFirst: () => void;
    /** Drops the container and its content from every action, and returns the steps to their initial state. */
    removePosition: () => void;
}

export interface UseContainerOrderOptions {
    data: ApiRequisition;
    apiResourcePath: string;
    /** What to call the container type and the material the order links. */
    naming: LinkNaming;
    /** The stock at the site, which is what tells a picked line apart from a described container. */
    stockLines: readonly ContainerStockLine[];
    /** Whether the wizard runs with containers at all. With the flag off nothing is written. */
    enabled: boolean;
    canEdit: boolean;
    requestUpdateState: AsyncStateUpdate<ApiRequisition>;
}

const NO_ROWS: readonly ApiRequisitionContainerAction[] = [];

/**
 * The action of the order being placed and the container actions under it.
 *
 * This sits above the wizard steps because the stepper label and the footer counter read from it,
 * and it is server-backed like every other draft field: the container actions are sub-objects of
 * the requisition, so every answer the customer gives is saved as they give it and survives a
 * reload. There is nothing to confirm — "Weiter" is what says the step is done. It is the only
 * place that writes `container_actions`; no step issues a call of its own.
 *
 * The content is a link on the container action as well. A container order carries no requisition
 * position at all, so the lines a loose-material order left behind are dropped once a container
 * action is chosen. Its first line is the positions step's to add.
 */
// @ai-generated 2026-09-25T08:48:50+00:00 by matthias.gessinger@synqony.com using Claude Opus 5.5 (1M context)
export function useContainerOrder({
    data,
    apiResourcePath,
    naming,
    stockLines,
    enabled,
    canEdit,
    requestUpdateState,
}: UseContainerOrderOptions): ContainerOrder {
    const hasPermission = useHasPermission();
    const requisitionPath = `${apiResourcePath}/${data.id}`;
    const rows = enabled ? data.container_actions : NO_ROWS;
    const ordered = useMemo(() => orderedRows(rows), [rows]);

    const { deleteSubObjects, requestUpdateSubObject } = useEditSubObject<
        ApiRequisitionContainerAction,
        ApiRequisition
    >(requisitionPath, 'container_actions', requestUpdateState);

    // A container order carries no positions, so choosing one drops the lines a loose-material order
    // left behind.
    const { deleteSubObjects: deletePositions } = useEditSubObject<ApiRequisitionPosition, ApiRequisition>(
        requisitionPath,
        'positions',
        requestUpdateState,
    );

    const planningTarget = useMemo(
        () => ({ kind: 'requisition' as const, id: data.id, requestUpdateState }),
        [data.id, requestUpdateState],
    );
    const { plan } = useContainerPlanning(planningTarget);

    // Loose material leaves no row behind, and a row that has just been minted carries no action
    // yet, so the tile the customer clicked is remembered here and the stored action restores it
    // after a reload.
    const [chosen, setChosen] = useState<RequisitionAction | undefined>(undefined);
    // The same answer where the next click can already see it: state only arrives with the next
    // render, and a double click is faster than that.
    const chosenRef = useRef<RequisitionAction | undefined>(undefined);
    const isExchange = ordered.length > 1;
    const storedAction = useMemo<RequisitionAction | undefined>(() => {
        if (isExchange) {
            return 'swap';
        }

        return ordered[0] ? fromApiAction(ordered[0].action) : undefined;
    }, [isExchange, ordered]);
    const action = chosen ?? storedAction;

    // A placement names a type that stands nowhere yet, so the stock cannot say how it is managed.
    // The same lookup the order mask uses answers it, off the types the rows link.
    //
    // The types are master data behind their own permission. Without it the list stays empty so the
    // call never goes out: a refused one would answer 403 with an empty body, and the warning toast
    // that follows re-renders the wizard into asking again.
    const canReadContainerTypes = hasPermission(Permission.StammdatenContainerTypeRead);
    const containerTypeIds = useMemo(
        () => (canReadContainerTypes ? ordered.map((row) => row.container_type.foreign_key) : []),
        [canReadContainerTypes, ordered],
    );
    const management = useContainerTypeDetails(containerTypeIds);

    const slotOptions = { requisitionPath, naming, stockLines, management, canEdit, requestUpdateSubObject };
    const first = useContainerSlot({ row: ordered[0], ...slotOptions });
    const second = useContainerSlot({ row: ordered[1], ...slotOptions });
    const slots = useMemo(() => [first, second] as const, [first, second]);
    const positions = useMemo(() => slots.flatMap((slot) => (slot.position ? [slot.position] : [])), [slots]);

    const { readLatest, enqueue } = useWriteQueue(requestUpdateState);

    /** Applies a change to one container action and waits for it to land. */
    const saveAction = useCallback(
        (id: string, change: (current: ApiRequisitionContainerAction) => ApiRequisitionContainerAction) =>
            requestUpdateSubObject(async (current, save) => save(change(current)), id),
        [requestUpdateSubObject],
    );

    const chooseAction = useCallback(
        (next: RequisitionAction) => {
            if (!enabled || next === (chosenRef.current ?? action)) {
                return;
            }

            chosenRef.current = next;
            setChosen(next);

            void enqueue(async () => {
                const latest = await readLatest();
                const storedRows = latest.container_actions;
                const stored = storedRows[0];

                if (next !== 'swap' && !isContainerAction(next)) {
                    // A loose-material order holds no container action at all, so a stale row must
                    // not survive into the counter, the chip or the summary.
                    if (storedRows.length > 0) {
                        await deleteSubObjects(storedRows.map((entry) => entry.id));
                    }
                    return;
                }

                // A container order carries its content on the row, so the lines of a loose-material
                // order mean nothing under it.
                const positionIds = latest.positions.map((entry) => entry.id);
                if (positionIds.length > 0) {
                    await deletePositions(positionIds);
                }

                // An order is one intention, so what it held goes before the server plans the new action.
                if (stored) {
                    await deleteSubObjects(storedRows.map((entry) => entry.id));
                }

                await plan(toActionRequest(next));
            });
        },
        [enabled, action, enqueue, readLatest, deletePositions, deleteSubObjects, plan],
    );

    const takeStockLine = useCallback(
        (line: ContainerStockLine) => {
            if (!enabled) {
                return;
            }

            void enqueue(async () => {
                const stored = (await readLatest()).container_actions;
                const request = requestOf(stored);
                if (request === undefined) {
                    return;
                }

                await deleteSubObjects(stored.map((entry) => entry.id));
                await plan(request, line.source);

                const count = initialCountOf(line, request);
                const [planned] = orderedRows((await readLatest()).container_actions);
                if (planned && count > planned.count) {
                    await saveAction(planned.id, (current) => ({ ...current, count }));
                }
            });
        },
        [enabled, enqueue, readLatest, deleteSubObjects, plan, saveAction],
    );

    const clearActions = useCallback(
        (count?: number) => {
            if (!enabled) {
                return;
            }

            void enqueue(async () => {
                const latest = await readLatest();
                const [stored, next] = orderedRows(latest.container_actions).slice(0, count);
                if (stored) {
                    await saveAction(stored.id, clearedAction);
                }
                if (next) {
                    await saveAction(next.id, clearedAction);
                }
            });
        },
        [enabled, enqueue, readLatest, saveAction],
    );

    const removeFirst = useCallback(() => clearActions(1), [clearActions]);
    const removePosition = useCallback(() => clearActions(), [clearActions]);

    return useMemo(
        () => ({
            action,
            slots,
            positions,
            positionCount: positions.length,
            chooseAction,
            takeStockLine,
            removeFirst,
            removePosition,
        }),
        [action, slots, positions, chooseAction, takeStockLine, removeFirst, removePosition],
    );
}

/**
 * The row emptied of the container it carried and its content. The action stays — it is what the
 * row is there for.
 */
// @ai-generated 2026-09-16T00:00:00+00:00 by matthias.gessinger@synqony.com using Claude Opus 5 (1M context)
export function clearedAction(stored: ApiRequisitionContainerAction): ApiRequisitionContainerAction {
    return {
        ...stored,
        container_type: { ...stored.container_type, foreign_key: undefined, display_string: '' },
        container: { ...stored.container, foreign_key: undefined, display_string: '' },
        content: { ...stored.content, foreign_key: undefined, display_string: '' },
        count: MIN_COUNT,
        remarks: '',
    };
}

/** Runs the requisition's writes one after another. */
// @ai-generated 2026-10-21T11:23:08+00:00 by niklas.eicker@tngtech.com using Claude Opus 5.5 (1M context)
function useWriteQueue(requestUpdateState: AsyncStateUpdate<ApiRequisition>) {
    /**
     * The requisition as the queue has it, without writing anything.
     *
     * A write path runs on the answer of the write before it, which the render it closes over only
     * catches up with a tick later — so the row it works on is read back through the queue.
     */
    const readLatest = useCallback(
        () =>
            new Promise<ApiRequisition>((resolve) => {
                void requestUpdateState(async (latest) => {
                    resolve(latest);
                    return undefined;
                });
            }),
        [requestUpdateState],
    );

    /**
     * Runs the write paths one after another.
     *
     * A second tile clicked before the first answer is in must not start on the state the first
     * click is still changing: that is how a second container action gets minted, and the wizard
     * reads only the first one — the customer would never see the other, let alone delete it.
     */
    const queued = useRef<Promise<unknown>>(Promise.resolve());
    const enqueue = useCallback((write: () => Promise<void>) => {
        const running = queued.current.then(write);
        queued.current = running.catch(() => undefined);
        return running;
    }, []);

    return { readLatest, enqueue };
}

/** What the server is asked for to plan again what the stored rows are: an exchange, a pickup or an emptying. */
// @ai-generated 2026-10-09T23:00:00+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
function requestOf(rows: readonly ApiRequisitionContainerAction[]): EnumContainerActionRequest | undefined {
    if (exchangeOf(rows)) {
        return EnumContainerActionRequest.Exchange;
    }

    switch (rows[0]?.action) {
        case EnumContainerOrderAction.Collect:
            return EnumContainerActionRequest.Collect;
        case EnumContainerOrderAction.Empty:
            return EnumContainerActionRequest.Empty;
        default:
            return undefined;
    }
}

// @ai-generated 2026-10-08T22:00:00+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
function exchangeOf(rows: readonly ApiRequisitionContainerAction[]) {
    const halves = rows.filter(isExchangeHalf);
    const pickup = halves.find((half) => half.action !== EnumContainerOrderAction.Place);
    const delivery = halves.find((half) => half.action === EnumContainerOrderAction.Place);

    return pickup && delivery ? { pickup, delivery } : undefined;
}

/** The container actions the wizard works on: the first one, or on an exchange the pickup and then the delivery. */
// @ai-generated 2026-10-09T08:04:13+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
function orderedRows(rows: readonly ApiRequisitionContainerAction[]): ApiRequisitionContainerAction[] {
    const exchange = exchangeOf(rows);

    return exchange ? [exchange.pickup, exchange.delivery] : rows.slice(0, 1);
}
